import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getVideoById, getRelatedVideos } from '@/mocks/videos';
import { getCategoryBySlug } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const video = getVideoById(id || '');

  if (!video) {
    return (
      <>
        <SEOHead
          title="文章不存在 - 蘑菇视频"
          description="抱歉，您访问的文章不存在。"
          keywords="蘑菇视频,短视频"
          canonicalUrl="https://www.moguvideo.com/"
        />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">内容不存在</h1>
            <p className="text-sm text-foreground-600 mb-6">抱歉，您访问的内容已被删除或不存在</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = getCategoryBySlug(video.category);
  const relatedVideos = getRelatedVideos(video, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '蘑菇视频首页', item: 'https://www.moguvideo.com/' },
          { '@type': 'ListItem', position: 2, name: category ? `${category.name}频道` : '频道', item: `https://www.moguvideo.com/category/${video.category}` },
          { '@type': 'ListItem', position: 3, name: video.title, item: `https://www.moguvideo.com/article/${video.id}` },
        ],
      },
      {
        '@type': 'Article',
        headline: video.title,
        description: video.description,
        author: { '@type': 'Person', name: video.author },
        datePublished: video.publishDate,
        image: video.coverImage,
        url: `https://www.moguvideo.com/article/${video.id}`,
        publisher: { '@type': 'Organization', name: '蘑菇视频' },
        articleBody: video.content.replace(/<[^>]*>/g, ''),
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${video.title} - 蘑菇视频 | 蘑菇视频App下载`}
        description={video.description}
        keywords={video.tags.map((t) => `蘑菇视频${t}`).join(',') + ',蘑菇视频,蘑菇视频App下载'}
        canonicalUrl={`https://www.moguvideo.com/article/${video.id}`}
        ogType="article"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <Navbar />
      <main className="pt-16">
        <article className="bg-background-50">
          <div className="w-full px-4 md:px-6 py-6">
            <div className="max-w-4xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                {category && (
                  <>
                    <Link to={`/category/${video.category}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">{category.name}频道</Link>
                    <i className="ri-arrow-right-s-line text-xs"></i>
                  </>
                )}
                <span className="text-foreground-950 font-medium line-clamp-1">{video.title}</span>
              </nav>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 font-heading mb-4 leading-tight">
                {video.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-background-200 overflow-hidden">
                    <img src={video.authorAvatar} alt={video.author} className="w-full h-full object-cover" />
                  </div>
                  <span className="font-medium text-foreground-700">{video.author}</span>
                </div>
                <span className="text-foreground-300">|</span>
                <span>{video.publishDate}</span>
                <span className="text-foreground-300">|</span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {video.views > 10000 ? `${(video.views / 10000).toFixed(1)}万次观看` : `${video.views}次观看`}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-heart-line"></i>
                  {video.likes > 10000 ? `${(video.likes / 10000).toFixed(1)}万` : video.likes}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-8">
                {video.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs">
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="w-full rounded-xl overflow-hidden mb-8 bg-background-900">
                <img
                  src={video.coverImage}
                  alt={video.title}
                  title={`${video.title} - 蘑菇视频`}
                  className="w-full h-auto object-cover object-top max-h-[500px]"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed mb-10"
                dangerouslySetInnerHTML={{ __html: video.content }}
              />

              <div className="flex items-center justify-between py-6 border-t border-background-200">
                <div className="flex items-center gap-2 text-sm text-foreground-500">
                  <span>分享到：</span>
                  <a href="#" className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                    <i className="ri-wechat-line"></i>
                  </a>
                  <a href="#" className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                    <i className="ri-weibo-line"></i>
                  </a>
                  <a href="#" className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                    <i className="ri-link"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {relatedVideos.length > 0 && (
            <div className="bg-background-100 py-10 md:py-14">
              <div className="w-full px-4 md:px-6">
                <div className="max-w-7xl mx-auto">
                  <h3 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-6">
                    相关推荐
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                    {relatedVideos.map((relVideo) => (
                      <Link
                        key={relVideo.id}
                        to={`/article/${relVideo.id}`}
                        className="group block bg-background-50 rounded-xl overflow-hidden hover:bg-background-200/50 transition-colors duration-300 cursor-pointer"
                      >
                        <div className="relative w-full h-40 overflow-hidden">
                          <img
                            src={relVideo.coverImage}
                            alt={relVideo.title}
                            title={`${relVideo.title} - 蘑菇视频`}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                            {relVideo.duration}
                          </div>
                        </div>
                        <div className="p-3">
                          <h4 className="text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors mb-1">
                            {relVideo.title}
                          </h4>
                          <div className="flex items-center gap-2 text-xs text-foreground-500">
                            <span>{relVideo.author}</span>
                            <span>·</span>
                            <span className="flex items-center gap-1">
                              <i className="ri-eye-line text-[10px]"></i>
                              {relVideo.views > 10000 ? `${(relVideo.views / 10000).toFixed(1)}万` : relVideo.views}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}